/**
 * EmailComposer — block-based email designer.
 *
 * Block types: heading (h1/h2/h3), text (body paragraph), image, button,
 * link, video, divider, spacer, two-column, social-row.
 *
 * UX:
 *   • Inline "+ Add block" inserter between every block (and at the top
 *     and bottom) — popover lets the user pick a type and inserts at that
 *     exact position.
 *   • Drag-handle reorders blocks.
 *   • Per-block trash icon removes the block.
 *   • Empty state shows a single inserter prompting the first block.
 *   • Subject + preview text + sender name editable in the email header.
 *   • Persists across mount via parent-controlled `value`/`onChange` props
 *     OR falls back to internal state if uncontrolled.
 */
import { useState, useRef, useEffect } from "react";
import {
  GripVertical, Type, Image as ImageIcon, Link2, Video, Square as ButtonIcon,
  Trash2, Plus, Heading1, Heading2, Heading3, AlignLeft, Minus, MoveVertical,
  Columns, Share2, X,
} from "lucide-react";

const uid = () => `b-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

const BLOCK_DEFAULTS = {
  heading: { level: "h1", content: "Your headline goes here" },
  text:    { content: "Tell your story here. Use this block for body copy." },
  image:   { src: "", alt: "", caption: "" },
  button:  { label: "Read more", url: "https://example.com", align: "left" },
  link:    { label: "View the article", url: "https://example.com" },
  video:   { url: "", thumbnail: "", caption: "" },
  divider: {},
  spacer:  { size: "md" }, // sm | md | lg
  columns: {
    left:  { type: "text", content: "Left column copy." },
    right: { type: "text", content: "Right column copy." },
  },
  social:  {
    items: [
      { network: "linkedin", url: "" },
      { network: "instagram", url: "" },
      { network: "twitter", url: "" },
    ],
  },
};

const BLOCK_PALETTE = [
  { type: "heading", label: "Heading", icon: Heading1, hint: "H1 / H2 / H3" },
  { type: "text",    label: "Body text", icon: AlignLeft, hint: "Paragraph" },
  { type: "image",   label: "Image", icon: ImageIcon, hint: "Photo or graphic" },
  { type: "button",  label: "Button", icon: ButtonIcon, hint: "Call to action" },
  { type: "link",    label: "Link", icon: Link2, hint: "Plain hyperlink" },
  { type: "video",   label: "Video", icon: Video, hint: "YouTube / Vimeo" },
  { type: "divider", label: "Divider", icon: Minus, hint: "Horizontal rule" },
  { type: "spacer",  label: "Spacer", icon: MoveVertical, hint: "Vertical gap" },
  { type: "columns", label: "Two columns", icon: Columns, hint: "Side-by-side" },
  { type: "social",  label: "Social row", icon: Share2, hint: "Follow icons" },
];

const newBlock = (type) => ({ id: uid(), type, ...JSON.parse(JSON.stringify(BLOCK_DEFAULTS[type] || {})) });

export default function EmailComposer({ value, onChange }) {
  const isControlled = !!value && typeof onChange === "function";
  const [internal, setInternal] = useState(() => value || {
    subject: "",
    preview: "",
    fromName: "Enable Healthcare",
    fromEmail: "hello@enablehealthcare.com",
    blocks: [],
  });

  const state = isControlled ? value : internal;
  const update = (patch) => {
    const next = typeof patch === "function" ? patch(state) : { ...state, ...patch };
    if (isControlled) onChange(next);
    else setInternal(next);
  };

  const [dragId, setDragId] = useState(null);
  const [inserterAt, setInserterAt] = useState(null); // index where inserter is open

  const setBlocks = (mut) => update((s) => ({ ...s, blocks: typeof mut === "function" ? mut(s.blocks) : mut }));

  const insertBlockAt = (idx, type) => {
    setBlocks((blocks) => {
      const next = [...blocks];
      next.splice(idx, 0, newBlock(type));
      return next;
    });
    setInserterAt(null);
  };

  const removeBlock = (id) => setBlocks((b) => b.filter((x) => x.id !== id));
  const updateBlock = (id, patch) => setBlocks((b) => b.map((x) => (x.id === id ? { ...x, ...patch } : x)));

  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (overId) => {
    if (!dragId || dragId === overId) return setDragId(null);
    setBlocks((blocks) => {
      const fromIdx = blocks.findIndex((b) => b.id === dragId);
      const toIdx = blocks.findIndex((b) => b.id === overId);
      if (fromIdx < 0 || toIdx < 0) return blocks;
      const next = [...blocks];
      const [moved] = next.splice(fromIdx, 1);
      next.splice(toIdx, 0, moved);
      return next;
    });
    setDragId(null);
  };

  return (
    <div className="email-composer" data-testid="email-composer">
      {/* Email-level meta: sender + subject + preview */}
      <div className="email-meta" data-testid="email-meta">
        <label>From name</label>
        <input
          value={state.fromName || ""}
          onChange={(e) => update({ fromName: e.target.value })}
          className="email-meta-input"
          data-testid="email-from-name"
          placeholder="Enable Healthcare"
        />
        <label>From email</label>
        <input
          value={state.fromEmail || ""}
          onChange={(e) => update({ fromEmail: e.target.value })}
          className="email-meta-input"
          data-testid="email-from-email"
          placeholder="hello@your-domain.com"
        />
        <label>Subject line</label>
        <input
          value={state.subject || ""}
          onChange={(e) => update({ subject: e.target.value })}
          className="email-meta-input"
          data-testid="email-subject"
          placeholder="A clear, compelling subject line"
        />
        <label>Preview text</label>
        <input
          value={state.preview || ""}
          onChange={(e) => update({ preview: e.target.value })}
          className="email-meta-input"
          data-testid="email-preview"
          placeholder="One-line tease that shows in the inbox"
        />
      </div>

      {/* Canvas */}
      <div className="email-canvas">
        <div className="email-header">
          <span className="email-from">{state.fromName} &lt;{state.fromEmail}&gt;</span>
          <span className="email-subj">{state.subject || "Subject line preview"}</span>
          <span className="email-prev">{state.preview || "Preview text preview"}</span>
        </div>

        <div className="email-body">
          {/* Top inserter (always visible) */}
          <BlockInserter
            open={inserterAt === 0}
            onOpen={() => setInserterAt(0)}
            onClose={() => setInserterAt(null)}
            onPick={(t) => insertBlockAt(0, t)}
            testid="email-inserter-top"
            emphasis={state.blocks.length === 0}
          />

          {state.blocks.map((b, idx) => (
            <div key={b.id}>
              <div
                draggable
                onDragStart={() => onDragStart(b.id)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(b.id)}
                className={`email-block email-block-${b.type}`}
                data-testid={`email-block-${b.type}`}
                data-block-id={b.id}
              >
                <div className="email-block-grip" title="Drag to reorder">
                  <GripVertical style={{ width: 12, height: 12 }} />
                </div>
                <button
                  onClick={() => removeBlock(b.id)}
                  className="email-block-del"
                  title="Delete block"
                  data-testid={`email-block-${b.type}-delete`}
                >
                  <Trash2 style={{ width: 11, height: 11 }} />
                </button>
                <BlockEditor block={b} onChange={(patch) => updateBlock(b.id, patch)} />
              </div>

              {/* Inline inserter between blocks */}
              <BlockInserter
                open={inserterAt === idx + 1}
                onOpen={() => setInserterAt(idx + 1)}
                onClose={() => setInserterAt(null)}
                onPick={(t) => insertBlockAt(idx + 1, t)}
                testid={`email-inserter-${idx + 1}`}
              />
            </div>
          ))}
        </div>

        <div className="email-footer">
          <p>You're receiving this because you opted in.<br />
            <a href="#unsub">Unsubscribe</a> · <a href="#prefs">Manage preferences</a>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ── Inline + Add block inserter ───────────────────────────────────────── */

function BlockInserter({ open, onOpen, onClose, onPick, testid, emphasis }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) onClose?.(); };
    const onEsc = (e) => { if (e.key === "Escape") onClose?.(); };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open, onClose]);

  if (!open) {
    return (
      <button
        type="button"
        onClick={onOpen}
        className={`email-inserter-line ${emphasis ? "is-empty" : ""}`}
        data-testid={testid}
        aria-label="Add block"
      >
        <span className="email-inserter-rule" />
        <span className="email-inserter-plus">
          <Plus style={{ width: 12, height: 12 }} />
          {emphasis ? <span style={{ marginLeft: 6 }}>Add your first block</span> : <span style={{ marginLeft: 6 }}>Add block</span>}
        </span>
        <span className="email-inserter-rule" />
      </button>
    );
  }

  return (
    <div className="email-inserter-popover" ref={ref} data-testid={`${testid}-popover`}>
      <div className="email-inserter-popover-head">
        <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text-tertiary)", textTransform: "uppercase", letterSpacing: 0.3 }}>
          Insert block
        </span>
        <button
          type="button"
          onClick={onClose}
          className="email-inserter-close"
          aria-label="Close"
          data-testid={`${testid}-close`}
        >
          <X style={{ width: 12, height: 12 }} />
        </button>
      </div>
      <div className="email-inserter-grid">
        {BLOCK_PALETTE.map((t) => {
          const Icon = t.icon;
          return (
            <button
              type="button"
              key={t.type}
              onClick={() => onPick(t.type)}
              className="email-inserter-tile"
              data-testid={`email-inserter-pick-${t.type}`}
              title={t.hint}
            >
              <Icon style={{ width: 16, height: 16, color: "var(--blue)" }} />
              <span className="email-inserter-tile-label">{t.label}</span>
              <span className="email-inserter-tile-hint">{t.hint}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ── Per-block editors ─────────────────────────────────────────────────── */

function BlockEditor({ block, onChange }) {
  if (block.type === "heading") {
    const Tag = block.level === "h2" ? "h2" : block.level === "h3" ? "h3" : "h1";
    return (
      <>
        <div className="email-block-toolbar">
          {["h1", "h2", "h3"].map((lvl) => (
            <button
              type="button"
              key={lvl}
              onClick={() => onChange({ level: lvl })}
              className={`tag ${block.level === lvl ? "tag-blue" : "tag-gray"}`}
              style={{ cursor: "pointer" }}
              data-testid={`heading-level-${lvl}`}
            >
              {lvl.toUpperCase()}
            </button>
          ))}
        </div>
        <Tag
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => onChange({ content: e.currentTarget.textContent })}
          className={`email-text-${block.level}`}
          style={{ outline: "none", margin: "4px 0" }}
        >
          {block.content}
        </Tag>
      </>
    );
  }
  if (block.type === "text") {
    return (
      <p
        contentEditable
        suppressContentEditableWarning
        onBlur={(e) => onChange({ content: e.currentTarget.textContent })}
        className="email-text-p"
        style={{ outline: "none", margin: 0 }}
      >
        {block.content}
      </p>
    );
  }
  if (block.type === "image") {
    return (
      <>
        {block.src ? (
          <img src={block.src} alt={block.alt || ""} className="email-img" />
        ) : (
          <div className="email-img-placeholder" data-testid="email-img-placeholder">
            <ImageIcon style={{ width: 22, height: 22, opacity: 0.5 }} />
            <span style={{ fontSize: 11, color: "var(--text-tertiary)", marginTop: 6 }}>Paste an image URL below</span>
          </div>
        )}
        <input
          value={block.src || ""}
          onChange={(e) => onChange({ src: e.target.value })}
          className="email-inline-input"
          placeholder="Image URL (https://…)"
        />
        <input
          value={block.alt || ""}
          onChange={(e) => onChange({ alt: e.target.value })}
          className="email-inline-input"
          placeholder="Alt text (for accessibility)"
          style={{ marginTop: 4 }}
        />
        <input
          value={block.caption || ""}
          onChange={(e) => onChange({ caption: e.target.value })}
          className="email-inline-input"
          placeholder="Optional caption"
          style={{ marginTop: 4 }}
        />
        {block.caption && <div style={{ fontSize: 11.5, color: "var(--text-tertiary)", marginTop: 6, textAlign: "center" }}>{block.caption}</div>}
      </>
    );
  }
  if (block.type === "button") {
    return (
      <>
        <div style={{ textAlign: block.align || "left", padding: "6px 0" }}>
          <a href={block.url || "#"} className="email-cta-btn">{block.label || "Click here"}</a>
        </div>
        <div className="email-block-toolbar">
          {["left", "center", "right"].map((a) => (
            <button
              type="button"
              key={a}
              onClick={() => onChange({ align: a })}
              className={`tag ${block.align === a ? "tag-blue" : "tag-gray"}`}
              style={{ cursor: "pointer", textTransform: "capitalize" }}
              data-testid={`button-align-${a}`}
            >{a}</button>
          ))}
        </div>
        <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
          <input
            value={block.label || ""}
            onChange={(e) => onChange({ label: e.target.value })}
            className="email-inline-input"
            placeholder="Button label"
            style={{ flex: 1 }}
          />
          <input
            value={block.url || ""}
            onChange={(e) => onChange({ url: e.target.value })}
            className="email-inline-input"
            placeholder="https://destination-url"
            style={{ flex: 1.5 }}
          />
        </div>
      </>
    );
  }
  if (block.type === "link") {
    return (
      <>
        <a href={block.url || "#"} className="email-link" style={{ color: "var(--blue, #2E63B8)", textDecoration: "underline" }}>
          {block.label || "Link text"} →
        </a>
        <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
          <input value={block.label || ""} onChange={(e) => onChange({ label: e.target.value })} className="email-inline-input" placeholder="Link text" style={{ flex: 1 }} />
          <input value={block.url || ""} onChange={(e) => onChange({ url: e.target.value })} className="email-inline-input" placeholder="URL" style={{ flex: 1.5 }} />
        </div>
      </>
    );
  }
  if (block.type === "video") {
    return (
      <>
        <div style={{ position: "relative", borderRadius: 10, overflow: "hidden", background: "var(--subtle)", aspectRatio: "16 / 9" }}>
          {block.thumbnail ? (
            <img src={block.thumbnail} alt="" className="email-img" style={{ position: "absolute", inset: 0 }} />
          ) : (
            <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-tertiary)", fontSize: 11 }}>
              Paste a thumbnail URL or video link
            </div>
          )}
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: block.thumbnail ? "rgba(0,0,0,0.25)" : "transparent" }}>
            <div style={{ width: 54, height: 54, borderRadius: "50%", background: "rgba(255,255,255,0.95)", display: "flex", alignItems: "center", justifyContent: "center", color: "var(--blue, #2E63B8)" }}>
              <Video style={{ width: 22, height: 22, fill: "currentColor" }} />
            </div>
          </div>
        </div>
        <input value={block.url || ""} onChange={(e) => onChange({ url: e.target.value })} className="email-inline-input" placeholder="Video URL (YouTube/Vimeo)" style={{ marginTop: 6 }} />
        <input value={block.thumbnail || ""} onChange={(e) => onChange({ thumbnail: e.target.value })} className="email-inline-input" placeholder="Thumbnail URL (optional)" style={{ marginTop: 4 }} />
        <input value={block.caption || ""} onChange={(e) => onChange({ caption: e.target.value })} className="email-inline-input" placeholder="Caption (optional)" style={{ marginTop: 4 }} />
      </>
    );
  }
  if (block.type === "divider") {
    return <hr style={{ border: "none", borderTop: "1px solid var(--border)", margin: "8px 0" }} />;
  }
  if (block.type === "spacer") {
    const px = { sm: 16, md: 32, lg: 64 }[block.size] || 32;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <div style={{ flex: 1, height: px, border: "1px dashed var(--border)", borderRadius: 6, background: "var(--subtle, #f5f5f7)" }} />
        <div className="email-block-toolbar" style={{ flexDirection: "column", gap: 4 }}>
          {["sm", "md", "lg"].map((sz) => (
            <button
              type="button"
              key={sz}
              onClick={() => onChange({ size: sz })}
              className={`tag ${block.size === sz ? "tag-blue" : "tag-gray"}`}
              style={{ cursor: "pointer", textTransform: "uppercase" }}
              data-testid={`spacer-${sz}`}
            >{sz}</button>
          ))}
        </div>
      </div>
    );
  }
  if (block.type === "columns") {
    return (
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {["left", "right"].map((side) => (
          <div key={side} style={{ borderLeft: "2px solid var(--border-soft, #e5e7eb)", paddingLeft: 12 }}>
            <p
              contentEditable
              suppressContentEditableWarning
              onBlur={(e) => onChange({ [side]: { ...block[side], content: e.currentTarget.textContent } })}
              className="email-text-p"
              style={{ outline: "none", margin: 0, minHeight: 36 }}
            >
              {block[side]?.content || ""}
            </p>
          </div>
        ))}
      </div>
    );
  }
  if (block.type === "social") {
    return (
      <div>
        <div style={{ display: "flex", gap: 10, marginBottom: 8 }}>
          {(block.items || []).map((it, i) => (
            <a key={i} href={it.url || "#"} style={{
              width: 30, height: 30, borderRadius: "50%", background: "var(--subtle, #f5f5f7)",
              display: "inline-flex", alignItems: "center", justifyContent: "center",
              color: "var(--text-secondary)", fontSize: 10, textTransform: "uppercase", fontWeight: 600,
            }}>
              {it.network.slice(0, 2)}
            </a>
          ))}
        </div>
        {(block.items || []).map((it, i) => (
          <div key={i} style={{ display: "flex", gap: 6, marginTop: 4 }}>
            <input
              value={it.network}
              onChange={(e) => {
                const next = [...(block.items || [])];
                next[i] = { ...next[i], network: e.target.value };
                onChange({ items: next });
              }}
              className="email-inline-input"
              placeholder="Network (e.g. linkedin)"
              style={{ flex: 1 }}
            />
            <input
              value={it.url}
              onChange={(e) => {
                const next = [...(block.items || [])];
                next[i] = { ...next[i], url: e.target.value };
                onChange({ items: next });
              }}
              className="email-inline-input"
              placeholder="Profile URL"
              style={{ flex: 2 }}
            />
          </div>
        ))}
      </div>
    );
  }
  return null;
}
